import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {RadioButton} from "primereact/radiobutton";
import listIcon from "../../../../../resources/images/splitmodes/list.png";
import nthIcon from "../../../../../resources/images/splitmodes/nth.png";
import groupIcon from "../../../../../resources/images/splitmodes/group.png";
import separateIcon from "../../../../../resources/images/splitmodes/saparate.png";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import DocumentSettings from "../../../document/DocumentSettings";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	SplitMode,
	ToolboxDeleteDelete,
	ToolboxDeleteDeleteInterface,
	ToolboxSplitSplit
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PageExtractionDialogFields from "../../field/export/PageExtractionDialogFields";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PortalToolboxSplitSplit} from "../../../../shared/models/PortalToolboxSplitSplit";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface PageExtractionDialogStatus extends PortalDialogState<PortalToolboxSplitSplit> {
	pages: number;
	currentPage: string;
}

export class PageExtractionDialogDefinition extends PortalDialog<PortalToolboxSplitSplit,
	PortalDialogProperties<PortalToolboxSplitSplit>, PageExtractionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<PageExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.extractPages, Functions.getExtractPagesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_PAGE, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxSplitSplit | undefined = this.state.parameter?.data;
		let errors: Map<PageExtractionDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(PageExtractionDialogFields.TYPE)}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageExtractionDialogFields.TYPE_SELECTED)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-type-selected">
							<Translate {...this.props} data="extract_page.type.selected.label" namespace="dialog"/>
							<Info {...this.props} data="extract_page.type.selected.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-type-selected" value={SplitMode.Single} name="mode"
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.TYPE_SELECTED, e.value
										 )}
										 checked={parameter?.mode === SplitMode.Single}/>
							<label htmlFor="control-type-selected" className="p-radiobutton-label">
								<img src={listIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value(
										 "extract_page.type.selected.alt", {ns: "dialog"}
									 )}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageExtractionDialogFields.TYPE_VARIABLE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-type-variable">
							<Translate {...this.props} data="extract_page.type.variable.label" namespace="dialog"/>
							<Info {...this.props} data="extract_page.type.variable.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-type-variable" value={SplitMode.Each} name="mode"
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.TYPE_VARIABLE, e.value
										 )}
										 checked={parameter?.mode === SplitMode.Each}/>
							<label htmlFor="control-type-variable" className="p-radiobutton-label">
								<img src={nthIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value(
										 "extract_page.type.variable.alt", {ns: "dialog"}
									 )}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageExtractionDialogFields.TYPE_GROUP)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-type-group">
							<Translate {...this.props} data="extract_page.type.group.label" namespace="dialog"/>
							<Info {...this.props} data="extract_page.type.group.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-type-group" value={SplitMode.Group} name="mode"
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.TYPE_GROUP, e.value
										 )}
										 checked={parameter?.mode === SplitMode.Group}/>
							<label htmlFor="control-type-group" className="p-radiobutton-label">
								<img src={groupIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value(
										 "extract_page.type.group.alt", {ns: "dialog"}
									 )}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageExtractionDialogFields.TYPE_SEPARATE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-type-separate">
							<Translate {...this.props} data="extract_page.type.separate.label" namespace="dialog"/>
							<Info {...this.props} data="extract_page.type.separate.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-type-separate" value={SplitMode.Burst} name="mode"
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.TYPE_SEPARATE, e.value
										 )}
										 checked={parameter?.mode === SplitMode.Burst}/>
							<label htmlFor="control-type-separate" className="p-radiobutton-label">
								<img src={separateIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value(
										 "extract_page.type.separate.alt", {ns: "dialog"}
									 )}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageExtractionDialogFields.DELETE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-delete"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-delete"
										 checked={FunctionHelper.applyValue(
											 parameter?.deleteSource, PortalToolboxSplitSplit.getDeleteSourceDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.DELETE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-delete">
								<Translate {...this.props} data="extract_page.delete.label" namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={parameter?.mode === SplitMode.Burst}>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(PageExtractionDialogFields.COUNT) ||
							 (
								 parameter?.mode !== SplitMode.Group &&
								 parameter?.mode !== SplitMode.Each
							 )
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-count"
							   hidden={parameter?.mode !== SplitMode.Group}>
							<Translate {...this.props} data="extract_page.pages.count" namespace="dialog"/>
						</label>
						<label className="p-col-12 p-sm-4" htmlFor="control-count"
							   hidden={parameter?.mode !== SplitMode.Each}>
							<Translate {...this.props} data="extract_page.pages.nth" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-count" step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.pageNumber, PortalToolboxSplitSplit.getPageNumberDefault()
										 )}
										 min={PortalToolboxSplitSplit.getPageNumberMin()}
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.COUNT, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(PageExtractionDialogFields.FROM) ||
							 (
								 parameter?.mode !== SplitMode.Group &&
								 parameter?.mode !== SplitMode.Each
							 )
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-from">
							<Translate {...this.props} data="extract_page.pages.from" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-from" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.startPage, ToolboxSplitSplit.getStartPageDefault()
										 )}
										 min={ToolboxSplitSplit.getStartPageMin()}
										 onChange={(e) => this.onChange(
											 PageExtractionDialogFields.FROM, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(PageExtractionDialogFields.PAGERANGE) ||
							 parameter?.mode !== SplitMode.Single
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="extract_page.pages.label" namespace="dialog"/>
							<Info {...this.props} data="extract_page.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(PageExtractionDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxSplitSplit.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   PageExtractionDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(PageExtractionDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(PageExtractionDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(PageExtractionDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(PageExtractionDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-page-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_page.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.extract_page.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PageExtractionDialogFields, value: any): void {
		let parameter: PortalToolboxSplitSplit = this.state.parameter!.data;
		let errors: Map<PageExtractionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case PageExtractionDialogFields.TYPE_SELECTED:
			case PageExtractionDialogFields.TYPE_VARIABLE:
			case PageExtractionDialogFields.TYPE_GROUP:
			case PageExtractionDialogFields.TYPE_SEPARATE:
				if (errors.has(PageExtractionDialogFields.PAGERANGE)) {
					errors.delete(PageExtractionDialogFields.PAGERANGE);
				}

				if (field === PageExtractionDialogFields.TYPE_SELECTED) {
					this.onChange(PageExtractionDialogFields.PAGERANGE, parameter.pages);
				}

				parameter.mode = value;
				break;
			case PageExtractionDialogFields.DELETE:
				parameter.deleteSource = value;
				break;
			case PageExtractionDialogFields.FROM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxSplitSplit.getStartPageDefault();
				}

				parameter.startPage = value;
				break;
			case PageExtractionDialogFields.COUNT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalToolboxSplitSplit.getPageNumberDefault();
				}

				parameter.pageNumber = value;
				break;
			case PageExtractionDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-export-page small"}/>
					<Translate {...this.props} data="extract_page.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxSplitSplit | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.pages = documentSettings.pageSelection.toString(pageCount, documentSettings.activeViewer.currentPage);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let pageExtractionParameter: PortalToolboxSplitSplit = this.state.parameter!.data.clone();
		if (pageExtractionParameter.mode !== SplitMode.Single) {
			pageExtractionParameter.pages = pageExtractionParameter.pageNumber!.toString();
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			split: pageExtractionParameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}
		}

		if (!pageExtractionParameter.deleteSource) {
			this.hide();
			return;
		}

		let documentSettings: DocumentSettings = selection[0].getSettings();
		let startPage: number = pageExtractionParameter.startPage!;
		let count: number = pageExtractionParameter.pageNumber!;
		let pageCount: number = await selection[0].getNumberOfPages();

		documentSettings.pageSelection.clearPageSelection();

		switch (pageExtractionParameter.mode) {
			case SplitMode.Each:
				while (startPage < pageCount) {
					documentSettings.pageSelection.selectPage(startPage, pageCount);
					startPage += count;
				}
				break;
			case SplitMode.Group:
				for (let i: number = startPage; i < pageCount + 1; i++) {
					documentSettings.pageSelection.selectPage(i, pageCount);
				}
				break;
			case SplitMode.Burst:
				documentSettings.pageSelection.selectAllPages(pageCount);
				break;
			default:
				documentSettings.pageSelection = PageSelection.fromString(pageExtractionParameter.pages!, pageCount)
				break;
		}

		let deletePageParameter: Parameter<ToolboxDeleteDelete> = new Parameter<ToolboxDeleteDelete>(
			ToolboxDeleteDelete.fromJson({
				pages: documentSettings.pageSelection.toString(pageCount, documentSettings.activeViewer.currentPage)
			} as ToolboxDeleteDeleteInterface)
		);

		toolboxOperation = BaseToolbox.fromJson({
			delete: deletePageParameter.data
		});

		operationData = [toolboxOperation];

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.extract.page", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_PAGE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxSplitSplit> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const PageExtractionDialog = React.memo(PageExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxSplitSplit>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxSplitSplit>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PageExtractionDialog;